import React, { useState, useCallback } from "react"
import { Tree } from "antd"
import type { DataNode } from "antd/es/tree"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import {
  faChevronRight,
  faChevronDown,
  faTable,
  faFont,
  faChartColumn,
  faImage,
  faHeading,
  faFolder,
  faChartPie,
  faChartLine,
} from "@fortawesome/free-solid-svg-icons"
import { IconDefinition } from "@fortawesome/fontawesome-svg-core"
import { ELEMENT_TYPE } from "types/ReportingDashboard/elements.types"

/**
 * Interface for draggable elements in the tree
 */
interface DraggableElementItem {
  title: string
  key: string
  icon: IconDefinition
  type: ELEMENT_TYPE
  text?: string
  children?: never // Draggable elements don't have children
}

/**
 * Interface for category items that contain draggable elements
 */
interface CategoryItem {
  title: string
  key: string
  icon: IconDefinition
  children: DraggableElementItem[]
  type?: never // Categories don't have types
  text?: never // Categories don't have text
}

/**
 * Union type for all tree items
 */
type TreeItem = DraggableElementItem | CategoryItem

/**
 * Interface for drag payload data
 */
interface DragPayload {
  type: ELEMENT_TYPE
  text?: string
}

/**
 * Props for the DataTreeItem component
 */
interface DataTreeItemProps {
  item: TreeItem
}

// Define the structure of elements available in the tree.
// This configuration drives the tree rendering and the data passed on drag.
const REPORTING_ELEMENT_CATEGORIES: CategoryItem[] = [
  {
    title: "Layout",
    key: "elements", // Keep original key if used elsewhere, or make more descriptive like 'layout'
    icon: faFolder, // Category icon
    children: [
      {
        title: "Text",
        key: "text",
        icon: faFont,
        type: ELEMENT_TYPE.TEXT, // This 'type' is used by ReportingCanvas on drop
        text: "Click to edit text", // Default text content for new elements
      },
      {
        title: "Heading",
        key: "heading",
        icon: faHeading,
        type: ELEMENT_TYPE.HEADING, // Type used by ReportingCanvas
        text: "Heading", // Default text content
      },
      {
        title: "Image",
        key: "image",
        icon: faImage,
        type: ELEMENT_TYPE.IMAGE, // Type used by ReportingCanvas
      },
    ],
  },
  {
    title: "Tables",
    key: "tables",
    icon: faFolder,
    children: [
      {
        title: "Crosstab",
        key: "xtab",
        icon: faTable,
        type: ELEMENT_TYPE.TABLE, // Both map to 'table' type for now
      },
      // {
      //   title: "DTAB",
      //   key: "dtab",
      //   icon: faTable, // Consider a different icon if functionality differs significantly
      //   type: ELEMENT_TYPE.TABLE, // Map to 'table' type
      // },
    ],
  },
  {
    title: "Charts",
    key: "chart", // Keep original key if used elsewhere, or 'charts'
    icon: faFolder,
    children: [
      {
        title: "Bar Chart",
        key: "bar",
        icon: faChartColumn,
        type: ELEMENT_TYPE.CHART, // Type used by ReportingCanvas
        // Optionally add default subtype if needed: chartSubType: 'bar'
      },
      {
        title: "Pie Chart",
        key: "pie",
        icon: faChartPie,
        type: ELEMENT_TYPE.PIE_CHART, // New type for PieChartElement
      },
      {
        title: "Time Series Chart",
        key: "timeseries",
        icon: faChartLine, // Or faChartColumn
        type: ELEMENT_TYPE.TIME_SERIES_CHART, // New type
      },
    ],
  },
]

/**
 * Internal component to render a single item (category or draggable element) in the tree.
 * Handles drag start logic for draggable elements.
 * Uses React.memo for performance optimization, as items only re-render if their props change.
 */
const DataTreeItem: React.FC<DataTreeItemProps> = React.memo(({ item }) => {
  // State to visually indicate when an item is being dragged
  const [isDragging, setIsDragging] = useState<boolean>(false)

  // Determine if the item is a category (has children) or a draggable element
  const isCategory = !!(item.children && item.children.length > 0)
  const isDraggable = !isCategory

  /**
   * Sets the data transfer payload when dragging a draggable element.
   * The payload contains information needed by ReportingCanvas to create the new element.
   */
  const handleDragStart = useCallback(
    (e: React.DragEvent<HTMLDivElement>): void => {
      if (!isDraggable) {
        e.preventDefault() // Prevent dragging categories
        return
      }
      setIsDragging(true) // Set visual state

      // Create the data payload (JSON string)
      const payload: DragPayload = {
        type: (item as DraggableElementItem).type, // Essential: The type of element to create
        text: (item as DraggableElementItem).text || "", // Include default text if defined
        // Add other properties if needed, e.g., chartSubType: item.chartSubType
      }
      e.dataTransfer.setData("text/plain", JSON.stringify(payload))
      e.dataTransfer.effectAllowed = "copy" // Indicate element creation
    },
    [isDraggable, item] // Dependencies for the callback
  )

  /** Resets the dragging visual state when the drag operation ends. */
  const handleDragEnd = useCallback((): void => {
    if (isDraggable) {
      setIsDragging(false)
    }
  }, [isDraggable])

  // Icon styling
  const iconColor = "#36c3ed"
  const iconStyle = "mr-2 w-4 text-center"

  return (
    // The main div for the tree item
    <div
      draggable={isDraggable} // Enable dragging only for elements
      onDragStart={handleDragStart}
      onDragEnd={handleDragEnd}
      // Apply dynamic classes based on state and type
      className={`
        flex items-center px-1 py-1.5 rounded
        ${
          isDraggable
            ? "cursor-move hover:bg-blue-100 transition-colors duration-150"
            : "cursor-default text-gray-700"
        }
        ${isDragging ? "opacity-50 border border-blue-300 bg-blue-100" : ""}
      `}
      // Tooltip to indicate action or category
      title={isDraggable ? `Drag to add ${item.title}` : item.title}
      role={isDraggable ? "button" : undefined} // Accessibility hint
      aria-label={typeof item.title === "string" ? item.title : undefined}
    >
      {/* Icon */}
      <FontAwesomeIcon
        icon={item.icon}
        className={iconStyle}
        // Apply color using inline style to maintain original appearance
        style={{ color: iconColor }}
        size="sm"
      />
      {/* Title Text */}
      <span
        className={`text-sm ${isCategory ? "font-medium" : ""}`} // Style category titles differently
      >
        {item.title}
      </span>
    </div>
  )
})

// Set display name for easier debugging in React DevTools
DataTreeItem.displayName = "DataTreeItem"

/**
 * Converts our TreeItem to Ant Design's DataNode format
 */
const convertToDataNode = (item: TreeItem): DataNode => {
  const baseNode: DataNode = {
    title: item.title,
    key: item.key,
  }

  if (item.children) {
    // Category item
    return {
      ...baseNode,
      children: item.children.map(convertToDataNode),
    }
  } else {
    // Draggable element item
    return baseNode
  }
}

/**
 * Renders the sidebar tree containing draggable reporting elements, organized by category.
 * Uses Ant Design's Tree component.
 */
const ReportingElementTree: React.FC = () => {
  // Convert our data structure to Ant Design's expected format
  const treeData = REPORTING_ELEMENT_CATEGORIES.map(convertToDataNode)

  // Store the original data in a map for titleRender lookup
  const itemLookupMap = new Map<string, TreeItem>()

  const buildLookupMap = (items: TreeItem[]): void => {
    items.forEach((item) => {
      itemLookupMap.set(item.key, item)
      if (item.children) {
        buildLookupMap(item.children)
      }
    })
  }

  buildLookupMap(REPORTING_ELEMENT_CATEGORIES)

  return (
    // Container div with original styling for layout and appearance
    <div className="w-64 border-r border-gray-200 flex-shrink-0 bg-white h-full overflow-y-auto">
      <div className="p-3">
        {/* Sidebar Title */}
        <h2 className="text-md font-semibold m-0 mb-3 px-1">Elements</h2>
        {/* Ant Design Tree Component */}
        <Tree
          treeData={treeData} // Provide the converted data
          showLine // Show connection lines
          showIcon={false} // Icons are rendered within the custom titleRender
          blockNode // Nodes take full width
          defaultExpandAll // Start with all categories expanded
          selectable={false} // Disable default node selection style
          // Custom icon for expanding/collapsing categories
          switcherIcon={({ expanded }: { expanded?: boolean }) => (
            <FontAwesomeIcon
              icon={expanded ? faChevronDown : faChevronRight}
              className="text-xs text-gray-400 hover:text-gray-600 transition-colors duration-150"
              style={{ marginRight: "4px" }}
            />
          )}
          // Use the custom DataTreeItem component to render each node's content
          titleRender={(nodeData: DataNode) => {
            const originalItem = itemLookupMap.get(nodeData.key as string)
            return originalItem ? (
              <DataTreeItem item={originalItem} />
            ) : (
              <span>
                {typeof nodeData.title === "string"
                  ? nodeData.title
                  : "Unknown Item"}
              </span>
            )
          }}
        />
      </div>
    </div>
  )
}

export default ReportingElementTree
